import React, { useState, useEffect, useMemo } from 'react';
import { Select, Input } from 'antd';

import { useSitesContext } from '@mono/mov-sites-context';
import { toHash } from '@mono/to-hash';
import { fuzzySearch } from '@mono/fuzzy-search';

import { sourceId, renderLayer, hoverLayer, generateSource } from './sites-mapbox-config';
import { SelectableList } from './selectable-list';
import { useSpaceConfiguratorContext } from 'space-configurator-context';

export const useSitesSelector = () => {
    const { state, enableSite, map, setState } = useSpaceConfiguratorContext();

    const { sites, fetchAllSites, sitesLoaded, key: sitesKey } = useSitesContext();
    const [layersHaveBeenSetup, setLayersHaveBeenSetup] = useState(false);
    const [lastClick, setLastClick] = useState<mapboxgl.Point>();

    useEffect(() => {
        if (!enableSite) return;
        if (sitesLoaded) return;
        fetchAllSites();
    }, []);

    useEffect(() => {
        if (!enableSite) return;
        if (!map) return;

        const existingSource = map.getSource(sourceId);
        if (existingSource && existingSource.type === 'geojson') {
            existingSource.setData(generateSource(sites));
        }
        if (!existingSource) {
            map.addSource(sourceId, {
                type: 'geojson',
                data: generateSource(sites),
                promoteId: 'id',
            });
        }

        const existingLayer = map.getLayer(renderLayer.id);
        if (!existingLayer) {
            map.addLayer(renderLayer);
        }

        const existingHoverLayer = map.getLayer(hoverLayer.id);
        if (!existingHoverLayer) {
            map.addLayer(hoverLayer);
        }

        setLayersHaveBeenSetup(true);
    }, [map, sitesKey]);

    useEffect(() => {
        if (!map) return;
        if (!enableSite) return;

        const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
            if (!layersHaveBeenSetup) return;
            if (!map.getLayer(hoverLayer.id)) return;
            const features = map.queryRenderedFeatures(e.point, {
                layers: [hoverLayer.id],
            });
            const source = map.getSource(sourceId);
            if (!source) return;

            const hoveredIds = features
                .map((feature) => {
                    if (!feature.properties) return null;
                    return feature.properties.movSiteId;
                })
                .filter((id): id is string => !!id);

            const notHoveredIds = (source as any)._data.features.map((feature: any) => feature.id).filter((id: any) => !hoveredIds.includes(id));
            for (const id of hoveredIds) {
                map.setFeatureState({ source: sourceId, id }, { hover: true });
            }
            for (const id of notHoveredIds) {
                map.setFeatureState({ source: sourceId, id }, { hover: false });
            }
        };

        const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
            if (!layersHaveBeenSetup) return;

            setLastClick(e.point);
        };

        const onZoomStart = () => {
            if (!layersHaveBeenSetup) return;

            setLastClick(undefined);
        };

        map.on('mousemove', onMouseMove);
        map.on('mousedown', onMouseDown);
        map.on('zoomstart', onZoomStart);

        return () => {
            map.off('mousemove', hoverLayer.id, onMouseMove);
            map.off('mousedown', hoverLayer.id, onMouseDown);
            map.off('zoomstart', onZoomStart);
        };
    }, [sitesKey, map, layersHaveBeenSetup]);

    const SitesSelectList = useMemo(() => {
        if (!map) return;
        if (!enableSite) return;
        if (!layersHaveBeenSetup) return;
        if (!lastClick) return;

        const features = map.queryRenderedFeatures(lastClick, {
            layers: [hoverLayer.id],
        });
        if (features.length === 0) {
            return;
        }

        if (features.length === 1) {
            const movSiteId = features[0].properties?.movSiteId;
            const site = sites.find((s) => s.movSiteId === movSiteId);
            if (!site) return;

            setState((prev) => ({ ...prev, movSiteId: site.movSiteId, space: { type: 'Point', coordinates: [site.coordinates.lon, site.coordinates.lat] } }));
            return;
        }
        const movSiteIds = features.map((f) => f.properties?.movSiteId).filter((a) => !!a) as string[];

        return (
            <SelectableList
                items={movSiteIds.map((movSiteId) => {
                    const site = sites.find((s) => s.movSiteId === movSiteId);
                    return { id: movSiteId, label: site?.name };
                })}
                onClick={(id) => {
                    const site = sites.find((s) => s.movSiteId === id);
                    if (!site) return;
                    setLastClick(undefined);
                    setState((prev) => ({
                        ...prev,
                        movSiteId: site.movSiteId,
                        space: { type: 'Point', coordinates: [site.coordinates.lon, site.coordinates.lat] },
                    }));
                }}
                style={{ position: 'absolute', top: lastClick.y, left: lastClick.x }}
            />
        );
    }, [toHash(lastClick), layersHaveBeenSetup]);

    useEffect(() => {
        if (!map) return;
        if (!layersHaveBeenSetup) return;

        const features = map.getSource(sourceId);
        if (!features) return;
        if (features.type !== 'geojson') return;
        for (const site of sites) {
            map.setFeatureState({ source: sourceId, id: site.movSiteId }, { isConsidering: false });
            if (site.movSiteId !== state.movSiteId) {
                map.setFeatureState({ source: sourceId, id: site.movSiteId }, { isSelected: false });
                continue;
            }
            map.setFeatureState({ source: sourceId, id: state.movSiteId }, { isSelected: true });
        }
    }, [sitesKey, state.movSiteId, layersHaveBeenSetup]);

    return {
        SitesSelectList,
    };
};

export const SitesList = () => {
    const { sites, fetchAllSites, sitesLoaded } = useSitesContext();
    const { state, setState, enableSite } = useSpaceConfiguratorContext();

    useEffect(() => {
        if (!enableSite) return;
        if (sitesLoaded) return;

        fetchAllSites();
    }, []);

    return (
        <Input.Group compact style={{ display: 'flex' }}>
            <Input disabled value={'Site'} style={{ width: 71, color: 'rgba(0, 0, 0, 0.85)', cursor: 'auto', marginRight: -1, textAlign: 'center' }} />
            <Select
                value={state.movSiteId}
                onChange={(value) => {
                    const site = sites.find((site) => site.movSiteId === value);
                    if (!site) return;
                    setState((prev) => ({
                        ...prev,
                        movSiteId: site.movSiteId,
                        space: { type: 'Point', coordinates: [site.coordinates.lon, site.coordinates.lat] },
                    }));
                }}
                style={{ flexGrow: 1 }}
                showSearch
                allowClear
                onClear={() => {
                    setState((prev) => ({ ...prev, movSiteId: undefined }));
                }}
                optionFilterProp='children'
                filterOption={(input, option) => fuzzySearch(input, [option!.children as unknown as string])}
                filterSort={(optionA, optionB) => (optionA!.children as unknown as string).localeCompare(optionB!.children as unknown as string)}
            >
                {sites.map((site) => (
                    <Select.Option key={site.movSiteId} value={site.movSiteId}>
                        {site.name}
                    </Select.Option>
                ))}
            </Select>
        </Input.Group>
    );
};
